import { PropertyService } from "../../services/PropertyService";
import { useApi } from "./useApi";
import { useConfiguration } from "./useConfiguration";

export const usePropertiesApi = () => {
    const { executeApiOperation } = useApi();
    const { appConfig } = useConfiguration();

    const propertyService = new PropertyService(appConfig.apiBaseUrl);

    const uploadFilesInChunks = async (files, uploadChunkCallback) => {
        const MAX_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB in bytes
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

        // Filter out files larger than 5MB
        const validFiles = files.filter(file => file.size <= MAX_FILE_SIZE);

        if (validFiles.length === 0) {
            return null;
        }

        // Group files into chunks where each chunk doesn't exceed 5MB
        const chunks = [];
        let currentChunk = [];
        let currentChunkSize = 0;

        for (const file of validFiles) {
            if (currentChunkSize + file.size > MAX_CHUNK_SIZE && currentChunk.length > 0) {
                // Start a new chunk
                chunks.push(currentChunk);
                currentChunk = [file];
                currentChunkSize = file.size;
            } else {
                currentChunk.push(file);
                currentChunkSize += file.size;
            }
        }

        // Add the last chunk
        if (currentChunk.length > 0) {
            chunks.push(currentChunk);
        }

        // Upload chunks sequentially and return the last response (which contains all pictures)
        let lastResult = null;
        for (const chunk of chunks) {
            lastResult = await uploadChunkCallback(chunk);
        }

        return lastResult;
    };

    // Property CRUD

    const findAllProperties = async () => {
        return executeApiOperation(() => propertyService.findAll());
    };

    const findPropertyById = async (id) => {
        return executeApiOperation(() => propertyService.findById(id));
    };

    const createProperty = async (property, logoFile) => {
        return executeApiOperation(() => propertyService.create(property, logoFile));
    };

    const updateProperty = async (id, property) => {
        return executeApiOperation(() => propertyService.update(id, property));
    };

    const deleteProperty = async (id) => {
        return executeApiOperation(() => propertyService.delete(id));
    };

    const uploadLogo = async (propertyId, file) => {
        return executeApiOperation(() => propertyService.uploadLogo(propertyId, file));
    };

    // Property pictures

    const getPictures = async (propertyId) => {
        return executeApiOperation(() => propertyService.getPictures(propertyId));
    };

    const getPicturesAlbum = async (propertyId, albumId) => {
        return executeApiOperation(() => propertyService.getPicturesAlbum(propertyId, albumId));
    };

    const createPicturesAlbum = async (propertyId, albumId, metadata) => {
        return executeApiOperation(() => propertyService.createPicturesAlbum(propertyId, albumId, metadata));
    };

    const uploadPictures = async (propertyId, albumId, files) => {
        return uploadFilesInChunks(files, (chunk) => {
            return executeApiOperation(() => propertyService.uploadPictures(propertyId, albumId, chunk));
        });
    };

    const updatePicturesAlbum = async (propertyId, albumId, metadata) => {
        return executeApiOperation(() => propertyService.updatePicturesAlbum(propertyId, albumId, metadata));
    };

    const deletePicturesAlbum = async (propertyId, albumId) => {
        return executeApiOperation(() => propertyService.deletePicturesAlbum(propertyId, albumId));
    };

    const deletePictures = async (propertyId, albumId, fileNames) => {
        return executeApiOperation(() => propertyService.deletePictures(propertyId, albumId, fileNames));
    };

    const getBankTransferConfig = async (propertyId) => {
        return executeApiOperation(() => propertyService.getBankTransferConfig(propertyId));
    };

    return {
        findAllProperties,
        findPropertyById,
        createProperty,
        updateProperty,
        deleteProperty,
        uploadLogo,
        getPictures,
        getPicturesAlbum,
        createPicturesAlbum,
        uploadPictures,
        updatePicturesAlbum,
        deletePicturesAlbum,
        deletePictures,
        getBankTransferConfig,
    };
};
